'use client';

import { c as _c } from "react/compiler-runtime";
import type { QueueMove } from '@/lib/code-sessions/turn-queue';
import type { QueryClient } from '@tanstack/react-query';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { CodeSessionController } from './session-controller';
import type { CodeQueuedTurnEdit, CodeSessionTurn } from './types';
import { codeSessionKeys } from './queries';
import { isRefusal } from './runtime/request-error';
import { getCodeSessionController } from './session-controller';

/** How saving an edit of a queued prompt ended, as edit mode handles it. */
export type QueuedEditOutcome = {
  kind: 'saved';
}
/** 409: a send claimed the prompt, so the agent may have it, or it was removed. */ | {
  kind: 'gone';
}
/** Another refusal, such as a skill or file the server turned away, with its reason. */ | {
  kind: 'refused';
  message: string;
}
/** The answer was lost or the server failed: the edit may have saved or not. */ | {
  kind: 'unconfirmed';
};
function failedEditOutcome(error: unknown): QueuedEditOutcome {
  if (!isRefusal(error)) {
    return {
      kind: 'unconfirmed'
    };
  }
  return error.status === 409 ? {
    kind: 'gone'
  } : {
    kind: 'refused',
    message: error.message
  };
}

/**
 * Writes the rows an edit or a move of queued follow-ups answered into the session's cache, after
 * cancelling any read of the session in flight: that read may predate the change, and would put
 * the old words or the old order back. It may also carry news the change does not, such as
 * another member's new turn or another tab's move, so a cancelled read runs again once the rows
 * are written.
 */
async function writeQueuedTurns(client: QueryClient, controller: CodeSessionController, turns: readonly CodeSessionTurn[]) {
  const queryKey = codeSessionKeys.detail(controller.sessionId);
  const reading = client.isFetching({
    queryKey,
    exact: true
  }) > 0;
  await client.cancelQueries({
    queryKey,
    exact: true
  });
  for (const turn of turns) {
    controller.saveTurn(turn);
  }
  if (reading) {
    void controller.refresh();
  }
}

/** Edits and moves of a session's queued follow-ups, as the composer's tray makes them. */
export type QueuedTurnWrites = {
  /** Save an edit of a queued follow-up. Resolves with how the save ended and never rejects. */
  saveEdit: (turnId: string, edit: CodeQueuedTurnEdit) => Promise<QueuedEditOutcome>;
  /**
   * Put a queued follow-up just before another, or last when `beforeTurnId` is null. Resolves once
   * the move settled and never rejects: a failure toasts the server's reason.
   */
  move: (turnId: string, beforeTurnId: string | null) => Promise<void>;
  /** A move runs. The tray makes one at a time. */
  moving: boolean;
};

/**
 * Edit and move a session's queued follow-ups. Nothing is optimistic: as in T3 Code, a row changes
 * when the server says so. The rows an edit or a move answered are in the cache before its promise
 * resolves (`writeQueuedTurns`). After a failure the session is read again, so the tray shows the
 * queue as it is, as after a 409 when a send claimed a turn first.
 * Edits and moves for the session serialize so older responses cannot overwrite newer writes.
 */
export function useQueuedTurnWrites(sessionId) {
  const $ = _c(45);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== sessionId) {
    t0 = getCodeSessionController(client, sessionId);
    $[0] = client;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const controller = t0;
  const t1 = `code-session-queued-writes:${sessionId}`;
  let t2;
  if ($[3] !== t1) {
    t2 = {
      id: t1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const scope = t2;
  let t3;
  if ($[5] !== sessionId) {
    t3 = codeSessionKeys.mutation(sessionId, "edit-queued");
    $[5] = sessionId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== controller) {
    t4 = t5 => {
      const {
        turnId,
        edit
      } = t5;
      return controller.conversation.editTurn(turnId, edit);
    };
    $[7] = controller;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== client || $[10] !== controller) {
    t5 = t6 => {
      const {
        turn
      } = t6;
      return writeQueuedTurns(client, controller, [turn]);
    };
    $[9] = client;
    $[10] = controller;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== controller) {
    t6 = () => {
      controller.refresh();
    };
    $[12] = controller;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== scope || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      scope,
      mutationKey: t3,
      retry: false,
      mutationFn: t4,
      onSuccess: t5,
      onError: t6
    };
    $[14] = scope;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    mutateAsync: sendEdit
  } = useMutation(t7);
  let t8;
  if ($[20] !== sendEdit) {
    t8 = (turnId_0, edit_0) => sendEdit({
      turnId: turnId_0,
      edit: edit_0
    }).then(_temp, failedEditOutcome);
    $[20] = sendEdit;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const saveEdit = t8;
  let t9;
  if ($[22] !== sessionId) {
    t9 = codeSessionKeys.mutation(sessionId, "move-queued");
    $[22] = sessionId;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const moveKey = t9;
  let t10;
  if ($[24] !== controller) {
    t10 = t11 => {
      const {
        turnId: turnId_1,
        beforeTurnId
      } = t11;
      return controller.conversation.moveTurn(turnId_1, beforeTurnId);
    };
    $[24] = controller;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== client || $[27] !== controller) {
    t11 = t12 => {
      const {
        turns
      } = t12;
      return writeQueuedTurns(client, controller, turns);
    };
    $[26] = client;
    $[27] = controller;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== controller) {
    t12 = error => {
      toast.error("Could not move queued message", {
        description: error.message
      });
      controller.refresh();
    };
    $[29] = controller;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== moveKey || $[32] !== scope || $[33] !== t10 || $[34] !== t11 || $[35] !== t12) {
    t13 = {
      scope,
      mutationKey: moveKey,
      retry: false,
      mutationFn: t10,
      onSuccess: t11,
      onError: t12
    };
    $[31] = moveKey;
    $[32] = scope;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const {
    mutateAsync: sendMove
  } = useMutation(t13);
  let t14;
  if ($[37] !== sendMove) {
    t14 = (turnId_2, beforeTurnId_0) => sendMove({
      turnId: turnId_2,
      beforeTurnId: beforeTurnId_0
    }).then(_temp2, _temp3);
    $[37] = sendMove;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const move = t14;
  let t15;
  if ($[39] !== moveKey) {
    t15 = {
      mutationKey: moveKey
    };
    $[39] = moveKey;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const moving = useIsMutating(t15) > 0;
  let t16;
  if ($[41] !== move || $[42] !== moving || $[43] !== saveEdit) {
    t16 = {
      saveEdit,
      move,
      moving
    };
    $[41] = move;
    $[42] = moving;
    $[43] = saveEdit;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
function _temp3() {}
function _temp2() {}
function _temp() {
  return {
    kind: "saved"
  };
}